import { HE_DRUG_GENERIC_FORMULA, JOINT_HOMUNCULUS_DATA } from 'src/consumer/constants'
import { getMPDataWithParams, postMPDataWithoutParams } from 'src/mentor/services'
import {
  getDrugItemOptionValue,
  getDrugTypeItemOptionValue,
} from './inputComponents/drugAdvice/utils'
import { getGenericDrugDataForDrugByFormulaId } from './prescriptionHelperFunctions'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { addOrUpdateDrugsInIdb, addOrUpdateGenericDrugsInIdb } from 'src/emr/database/indexedDbDao'

export const isHomunculusValid = (jointHomunculusData) => {
  return (
    Object.keys(JOINT_HOMUNCULUS_DATA).filter((key) => {
      return jointHomunculusData?.[key]
    })?.length > 0
  )
}

export function getDrugAdviceItems({ drugAdviceArray, keyOfItem }) {
  let items = drugAdviceArray?.map((item) => {
    return item?.[keyOfItem]
  })

  //keep items that have a label and are unique, label wise
  items = items
    ?.filter((item) => !!item?.label)
    ?.filter((item, index, self) => {
      return index === self.findIndex((t) => t.label === item.label)
    })

  console.log({ keyOfItem, items })

  return items
}

export async function getHealthEntityResponse({ healthEntityParam, params = {} }) {
  const response = await getMPDataWithParams(`/api/secure/clinic-practitioner/${healthEntityParam}/list`, params)
  return response
}

function formNewInternalDrugObjectWithSavedDrugItem({
  drug,
  savedWhatTimes,
  savedDurations,
  savedNotes,
  savedFrequencies,
  savedDosages,
  savedDrugType,
  prescriptionViewSettings,
}) {
  // if the drug items have __isNew__ property, then find the corresponding saved item by title to label search
  const foundWhatTime =
    savedWhatTimes.find(
      (item) => getDrugItemOptionValue(item, prescriptionViewSettings) === drug.whatTime?.label,
    ) || {}
  const foundDuration =
    savedDurations.find(
      (item) => getDrugItemOptionValue(item, prescriptionViewSettings) === drug.duration?.label,
    ) || {}
  const foundNote =
    savedNotes.find(
      (item) => getDrugItemOptionValue(item, prescriptionViewSettings) === drug.notes?.label,
    ) || {}
  const foundFrequency =
    savedFrequencies.find(
      (item) => getDrugItemOptionValue(item, prescriptionViewSettings) === drug.frequency?.label,
    ) || {}
  const foundDosage =
    savedDosages.find(
      (item) => getDrugItemOptionValue(item, prescriptionViewSettings) === drug.dosage?.label,
    ) || {}
  const foundDrugType =
    savedDrugType.find(
      (item) => getDrugTypeItemOptionValue(item, prescriptionViewSettings) === drug.drugType?.label,
    ) || {}

  return {
    ...drug,
    whatTime: { ...drug?.whatTime, ...foundWhatTime },
    duration: { ...drug?.duration, ...foundDuration },
    notes: { ...drug?.notes, ...foundNote },
    frequency: { ...drug?.frequency, ...foundFrequency },
    dosage: { ...drug?.dosage, ...foundDosage },
    drugType: { ...drug?.drugType, ...foundDrugType },
  }
}

export function formNewInternalDrugObjectWithDrugAdviceItems({
  allDrugs,
  whatTimes,
  durations,
  notes,
  frequencies,
  dosages,
  drugTypes,
  prescriptionViewSettings,
}) {
  return (
    allDrugs?.map((drug, index) => {
      return formNewInternalDrugObjectWithSavedDrugItem({
        drug,
        savedWhatTimes: whatTimes,
        savedDurations: durations,
        savedNotes: notes,
        savedFrequencies: frequencies,
        savedDosages: dosages,
        savedDrugType: drugTypes,
        prescriptionViewSettings,
      })
    }) || []
  )
}

function sortFunctionForHealthEntityOrder(a, b) {
  if (a.order === b.order) {
    return a.title.localeCompare(b.title)
  }
  return a.order - b.order
}

export function getOrderedDrugAdviceItemOptions({ drugAdviceItemOptions }) {
  let personalizedItems = drugAdviceItemOptions.filter((item) => item.clinicIds?.length > 0) || []
  let availableForAllItems =
    drugAdviceItemOptions.filter((item) => item.clinicIds?.length === 0 || !item.clinicIds) || []
  //sort items by the key order. if the key order is same for two items, then sort by title
  personalizedItems = personalizedItems.sort(sortFunctionForHealthEntityOrder)
  availableForAllItems = availableForAllItems.sort(sortFunctionForHealthEntityOrder)
  return [...personalizedItems, ...availableForAllItems]
}

export function getAvailableColumnsForDrugAdviceWithPercentageWidth({ drugAdviceArray }) {
  return []
}

export function getPrescriptionBreaksWithWhereInHeight({ a4PageHeight, prescriptionHeight }) {
  //gets the number of breaks in the prescription based on the height of the a4 page with there its breaking.
  //returns an array of the heights of the breaks
  const breaks = []
  let currentHeight = a4PageHeight
  while (currentHeight < prescriptionHeight) {
    breaks.push(currentHeight)
    currentHeight += a4PageHeight
  }
  return breaks
}

export function prepareLatestVital(vitals) {
  return vitals?.[0] || null
}

export async function getGenericDrugItemObject({
  genericNameId,
  genericNameTitle,
  doctorId,
  clinicBrandId,
  clinicId,
}) {
  let finalObject = null
  if (genericNameId) {
    finalObject = await getGenericDrugDataForDrugByFormulaId(genericNameId, genericNameTitle)
    if (finalObject?.title === genericNameTitle) {
      return null
    }
  }

  if (!finalObject) {
    finalObject = await createGenericNewGenericDrugObject({
      genericNameId,
      title: genericNameTitle,
    })
  }
  finalObject.mentorId = doctorId
  finalObject.title = genericNameTitle
  finalObject.clinicBrandId = clinicBrandId
  finalObject.clinicId = clinicId
  finalObject.availableToAll = false

  return finalObject
}

function createGenericNewGenericDrugObject({ genericNameId, title }) {
  return {
    id: genericNameId,
    title,
  }
}

export async function saveAndAddToIdbGenericNames({ genericDrugs, clinicId, clinicBrandId }) {
  const toReturn = []

  const promises = genericDrugs.map(async (item) => {
    const response = await postMPDataWithoutParams(
      `/api/secure/doctor/drug-generic-formula/save?clinicId=${clinicId}&clinicBrandId=${clinicBrandId}`,
      { ...item, apiParam: HE_DRUG_GENERIC_FORMULA },
    )
    let drugItem = item
    if (
      response?.status === 200 &&
      isCommonResponseSuccessful(response?.data?.code) &&
      response?.data?.data?.[HE_DRUG_GENERIC_FORMULA]?.id &&
      response?.data?.data?.[HE_DRUG_GENERIC_FORMULA]?.title
    ) {
      addOrUpdateGenericDrugsInIdb([response?.data?.data?.[HE_DRUG_GENERIC_FORMULA]])
      drugItem = response?.data?.data?.[HE_DRUG_GENERIC_FORMULA]
    }
    toReturn.push(drugItem)
  })

  await Promise.all(promises)
  return toReturn
}

export function buildHpRowItem({
  showWhen,
  showFrequency,
  showDuration,
  showNotes,
  isHeader = false,
  drugAdviceItem,
}) {
  let hpRowItems = []

  if (showWhen) {
    hpRowItems.push(isHeader ? 'When' : drugAdviceItem?.whatTime?.label)
  }
  if (showFrequency) {
    hpRowItems.push(isHeader ? 'Frequency' : drugAdviceItem?.frequency?.label)
  }
  if (showDuration) {
    hpRowItems.push(isHeader ? 'Duration' : drugAdviceItem?.duration?.label)
  }
  if (showNotes) {
    hpRowItems.push(isHeader ? 'Notes' : drugAdviceItem?.notes?.label)
  }

  return hpRowItems.length > 0 ? hpRowItems?.filter(item=>!!item).join(' - ') : ''
}
